/**
 * Refresh-state-on-move policy.
 *
 * On a State SRP, every settled map position refreshes listings, counts and pins for
 * the visible part of the state. The location type stays State, so the URL never
 * gains a `coordinates` parameter and the page is still the State page.
 *
 * Applies when:
 *  - the location type is State;
 *  - the position is not the initial one;
 *  - no State selection is pending; and
 *  - the search bounds overlap the state's bounding box.
 *
 * Decision: `refreshState(searchBounds clamped to the state)`. With interactive site
 * plans in view (zoom >= SITE_PLAN_RENDER_ZOOM_THRESHOLD) it returns `none` when every
 * visible grid cell was already fetched, so panning around a site plan does not
 * re-request the same area. `none` rather than `null` keeps the fallback from ending
 * focused mode.
 *
 * Events:
 *  - `start`, `boundsFromBackend`: bootstrap the state bounds.
 *  - `locationTypeChanged`: on entering State, bootstrap and, coming from another
 *    type, recenter and zoom to a state-wide view; on leaving, reset memory.
 *  - `stateChanged`: reset memory, mark the selection pending, recenter and zoom.
 *
 * Memory:
 *  - `stateBounds`: the state-wide bounding box, taken from the first backend bounds
 *    on a State page (more reliable than the initial location, which may only describe
 *    a viewport around the centroid), or from the location as a fallback.
 *  - `fetchedCells`: grid cells already fetched while interactive site plans are in view.
 *  - `stateSelectionPending`: set when the user picks a different state, cleared by
 *    that state's first backend response, so the camera reset does not trigger a
 *    duplicate refresh while the first request is in flight.
 *
 * It runs last: at focused zoom, focused search handles the position first and this
 * policy takes over again below it.
 *
 * Pure — no store, map, DOM or RxJS access.
 *
 * @module searchResultsMap/services/refreshStateOnMove.policy
 */

import { isPolygonInBounds } from './geometry';
import {
  SITE_PLAN_RENDER_ZOOM_THRESHOLD,
  STATE,
  STATE_DEFAULT_ZOOM,
} from '../constants';
import { none, recenter, refreshState, setZoom } from './decisions';
import { markViewportFetched, viewportHasUnfetchedCells } from './stateViewportGrid';
import getLocationBounds from './locationBounds';
import ViewportSearchEvent from '../enums/viewportSearchEvent';

function clampToState(viewport, stateBounds) {
  const clamped = {
    south: Math.max(viewport.south, stateBounds.south),
    north: Math.min(viewport.north, stateBounds.north),
    west: Math.max(viewport.west, stateBounds.west),
    east: Math.min(viewport.east, stateBounds.east),
  };

  // A degenerate rectangle means the viewport does not overlap the state.
  if (clamped.south >= clamped.north || clamped.west >= clamped.east) {
    return null;
  }

  return clamped;
}

function getStateResetIntents(location) {
  const latitude = parseFloat(location?.originLatitude);
  const longitude = parseFloat(location?.originLongitude);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return [];
  }

  return [recenter(latitude, longitude), setZoom(STATE_DEFAULT_ZOOM)];
}

/**
 * @param {object} options
 * @param {number} options.gridSize - Marker spatial index cell size, in decimal degrees.
 */
export default function createRefreshStateOnMovePolicy({ gridSize }) {
  const memory = {
    stateBounds: null,
    fetchedCells: new Set(),
    stateSelectionPending: false,
  };

  function resetMemory() {
    memory.stateBounds = null;
    memory.fetchedCells.clear();
  }

  function bootstrapFromLocation(location) {
    if (!memory.stateBounds) {
      memory.stateBounds = getLocationBounds(location);
    }
  }

  function evaluate(snapshot) {
    if (snapshot.locationType !== STATE || snapshot.isInitialPosition) {
      return null;
    }

    if (memory.stateSelectionPending || !memory.stateBounds) {
      return null;
    }

    const clampedBounds = clampToState(snapshot.searchBounds, memory.stateBounds);
    if (!clampedBounds) {
      return null;
    }

    const hasInteractiveSitePlanInView =
      snapshot.zoom >= SITE_PLAN_RENDER_ZOOM_THRESHOLD &&
      snapshot.sitePlans.some((sitePlan) => isPolygonInBounds(sitePlan, clampedBounds));

    if (!hasInteractiveSitePlanInView) {
      return refreshState(clampedBounds);
    }

    // With site plans in view, the visible set can only grow if some cell is new.
    // `none` (not `null`) keeps the fallback from ending focused mode by accident.
    if (!viewportHasUnfetchedCells(memory.fetchedCells, clampedBounds, gridSize)) {
      return none();
    }

    markViewportFetched(memory.fetchedCells, clampedBounds, gridSize);
    return refreshState(clampedBounds);
  }

  function handleBoundsFromBackend({ bounds, locationType }) {
    if (!memory.stateBounds && locationType === STATE && bounds) {
      memory.stateBounds = bounds;
    }

    memory.stateSelectionPending = false;
    return [];
  }

  function handleLocationTypeChanged({ from, to, location }) {
    if (to !== STATE) {
      resetMemory();
      return [];
    }

    bootstrapFromLocation(location);
    return from !== STATE ? getStateResetIntents(location) : [];
  }

  function handleStateChanged({ location }) {
    resetMemory();
    memory.stateSelectionPending = true;
    return getStateResetIntents(location);
  }

  function onEvent(event) {
    switch (event.type) {
      case ViewportSearchEvent.Start:
        if (event.locationType === STATE) {
          bootstrapFromLocation(event.location);
        }
        return [];
      case ViewportSearchEvent.BoundsFromBackend:
        return handleBoundsFromBackend(event);
      case ViewportSearchEvent.LocationTypeChanged:
        return handleLocationTypeChanged(event);
      case ViewportSearchEvent.StateChanged:
        return handleStateChanged(event);
      default:
        return [];
    }
  }

  return {
    name: 'refresh-state-on-move',
    evaluate,
    onEvent,
  };
}
